import { useEffect, useRef, useState } from "react";

import { useSchedulerApi } from "./api/scheduler";
import type { ReportCategory } from "./api/scheduler";
import { AddImageButton, PendingThumbnails, usePendingImages } from "./ImageAttach";

/**
 * "Report a problem", in the header, on every screen. The same control as
 * Fieldpath's, deliberately — somebody reporting the same confusion in both
 * apps should not have to learn two shapes, and the two exports read together.
 *
 * It asks for a category and what happened. Everything else the app fills in,
 * because it already knows: the route, who signed in, which build, and the
 * reference from the last error. Asking somebody to copy a URL out of the
 * address bar is exactly the friction that loses the report.
 *
 * **It works even for somebody this app has refused.** The report route takes a
 * verified token and does not require the roster link, because a person whose
 * email does not match the roster cannot use any of this — and is therefore the
 * most likely person in the beta to have something worth telling us.
 */
export function ReportProblem() {
  const api = useSchedulerApi();
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState<ReportCategory[] | null>(null);
  const [categoryId, setCategoryId] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  // Screenshots sent with the report (Jeremy, 3 Nov 2026), as Canopy's are.
  const images = usePendingImages(setError);

  // Loaded when the panel first opens: this sits on every screen and must cost
  // nothing until somebody wants it.
  useEffect(() => {
    if (!open || categories) return;
    void api
      .reportCategories()
      .then((list) => {
        setCategories(list);
        setCategoryId(list[0]?.id ?? "");
        setError(null);
      })
      // A report needs a category -- `report_category_id` is NOT NULL (009) and the route
      // requires it -- so Send stays off without the list. Until 22 Oct this said "you can
      // still describe the problem", which the disabled button then refused (Fieldpath #303
      // fixed its twin). Closing and opening the panel retries.
      .catch(() =>
        setError("Could not load the list, and a report needs it. Close this and open it again, or reload the page."),
      );
  }, [open, categories, api]);

  useEffect(() => {
    if (open) bodyRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  async function submit(): Promise<void> {
    if (!body.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await api.reportProblem({
        categoryId,
        body,
        route: window.location.pathname + window.location.search,
        reference: lastErrorReference(),
        appBuild: import.meta.env.VITE_BUILD_SHA ?? "dev",
        images: await images.payload(),
      });
      setSent(true);
      setBody("");
      images.clear();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not send that. Try again?");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button className="report-open" onClick={() => setOpen(true)} title="Report a problem">
        Report a problem
      </button>
    );
  }

  return (
    <div className="report-panel" role="dialog" aria-label="Report a problem">
      {sent ? (
        <div className="report-done">
          <p>
            <strong>Thank you — that is logged.</strong>
          </p>
          {/* No status and no ticket number on purpose: triage is GitHub Issues,
              and promising a thread here would mean keeping two records in step. */}
          <p className="muted">
            Jeremy reads these. Jeremy will come back to you directly if more
            is needed.
          </p>
          <div className="row">
            <button
              onClick={() => {
                setSent(false);
                setOpen(false);
              }}
            >
              Close
            </button>
            <button className="linkish" onClick={() => setSent(false)}>
              Report something else
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="row spread">
            <strong>Report a problem</strong>
            <button className="linkish" onClick={() => setOpen(false)} aria-label="Close">
              ×
            </button>
          </div>

          <label className="field">
            <span>What kind of thing is it?</span>
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              {(categories ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          {/* A screenshot pasted or dropped into the box goes with the report. */}
          <label className="field" {...images.dropTarget}>
            <span>What happened?</span>
            <textarea
              ref={bodyRef}
              rows={5}
              maxLength={4000}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="What you were doing, and what you expected instead. Paste or drop a screenshot here."
            />
          </label>

          <PendingThumbnails images={images} />

          <p className="muted small">
            We will also record which page you are on and which version you are
            using, so you do not have to.
          </p>

          {error ? <p className="error">{error}</p> : null}

          <div className="row">
            <button
              className="primary"
              disabled={!body.trim() || !categoryId || saving || images.shrinking > 0}
              onClick={() => void submit()}
            >
              {saving ? "Sending…" : "Send"}
            </button>
            <AddImageButton images={images} disabled={saving} />
            <button className="linkish" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The reference from the most recent error, stashed by the API client.
 * sessionStorage rather than state because the error and the report happen in
 * different components and often minutes apart.
 */
function lastErrorReference(): string | null {
  try {
    return window.sessionStorage.getItem("scheduler:lastErrorReference");
  } catch {
    return null;
  }
}
